import React, { useState, useEffect } from "react";
import {
  Box, VStack, HStack, Heading, Text, FormControl,
  FormLabel, Textarea, Select, Button,
  Radio, RadioGroup, Stack, Divider, Icon,
  useColorModeValue, useToast, Alert, AlertIcon,
  Badge,
} from "@chakra-ui/react";
import { FaPhone, FaEnvelope, FaHeadset, FaClock } from "react-icons/fa";
import axios from "axios";
import { useTenant } from "../Routers/TenantContextProvider";
import { StudentInfo } from "../Common/types";

const PHONE_SLOTS = [
  "Morning (9am – 12pm)",
  "Afternoon (12pm – 3pm)",
  "Evening (3pm – 6pm)",
];

const CATEGORIES = [
  "Enrolment & Registration",
  "Timetable & Batches",
  "Technical Issue",
  "Content & Lessons",
  "Assignments & Exams",
  "Payment & Billing",
  "Other",
];

const StudentSupportPage = () => {
  const { tenant } = useTenant();
  const [currentStudent, setCurrentStudent] = useState<StudentInfo | null>(null);

  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [preferredChannel, setPreferredChannel] = useState("email");
  const [phoneSlot, setPhoneSlot] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toast = useToast();
  const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";
  const token = localStorage.getItem("token");

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const infoBg = useColorModeValue("blue.50", "blue.900");
  const infoBorderColor = useColorModeValue("blue.100", "blue.700");
  const prefilledBg = useColorModeValue("gray.50", "gray.700");

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const response = await axios.get(`${apiUrl}/students-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCurrentStudent(response.data);
      } catch (error) {
        console.error("Error fetching student info:", error);
      }
    };
    fetchStudent();
  }, []);

  const handleSubmit = async () => {
    if (!tenant) return;

    if (!category) {
      toast({ title: "Please select a category", status: "warning", duration: 3000 });
      return;
    }
    if (!message.trim() || message.trim().length < 20) {
      toast({ title: "Please describe your issue in at least 20 characters", status: "warning", duration: 3000 });
      return;
    }
    if (preferredChannel === "phone" && !phoneSlot) {
      toast({ title: "Please select a preferred call time", status: "warning", duration: 3000 });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${apiUrl}/tenants/${tenant.tenant_id}/support-ticket`,
        {
          subject_category: category,
          message: message.trim(),
          preferred_channel: preferredChannel,
          preferred_phone_slot: preferredChannel === "phone" ? phoneSlot : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitted(true);
    } catch (error: any) {
      toast({
        title: error.response?.data?.detail ?? "Failed to submit. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box maxW="680px" mx="auto" p={6}>
        <Alert status="success" borderRadius="xl" p={6} flexDirection="column"
          alignItems="center" textAlign="center" gap={3}>
          <AlertIcon boxSize={8} />
          <Text fontWeight="bold" fontSize="lg">Query Received</Text>
          <Text fontSize="sm" color={mutedText}>
            Thank you for reaching out. We've sent a confirmation to{" "}
            <strong>{currentStudent?.email}</strong> with your ticket reference.
            {tenant?.tenant_name} will get back to you on your preferred channel as soon as possible.
          </Text>
          <Button mt={2} colorScheme="blue" size="sm"
            onClick={() => {
              setSubmitted(false);
              setCategory("");
              setMessage("");
              setPreferredChannel("email");
              setPhoneSlot("");
            }}>
            Submit Another Query
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box maxW="780px" mx="auto" p={6}>
      <VStack align="start" spacing={1} mb={8}>
        <Heading size="lg">Support</Heading>
        <Text color={mutedText}>
          We're here to help. Reach out and {tenant?.tenant_name ?? "we"}'ll get back to you promptly.
        </Text>
      </VStack>

      {/* Contact info strip — driven by tenant context, not env vars */}
      <Box bg={infoBg} borderRadius="xl" p={5} mb={6}
        borderWidth="1px" borderColor={infoBorderColor}>
        <HStack spacing={8} wrap="wrap">
          {tenant?.contact_phone && (
            <HStack spacing={3}>
              <Icon as={FaPhone} color="blue.500" />
              <Box>
                <Text fontSize="xs" color={mutedText}>Call us</Text>
                <Text fontWeight="semibold" fontSize="sm">{tenant.contact_phone}</Text>
              </Box>
            </HStack>
          )}
          <HStack spacing={3}>
            <Icon as={FaEnvelope} color="blue.500" />
            <Box>
              <Text fontSize="xs" color={mutedText}>Email us</Text>
              <Text fontWeight="semibold" fontSize="sm">
                {tenant?.support_email ?? tenant?.contact_email ?? "—"}
              </Text>
            </Box>
          </HStack>
          <HStack spacing={3}>
            <Icon as={FaClock} color="blue.500" />
            <Box>
              <Text fontSize="xs" color={mutedText}>Response time</Text>
              <Text fontWeight="semibold" fontSize="sm">Within 1 business day</Text>
            </Box>
          </HStack>
        </HStack>
      </Box>

      {/* Support form */}
      <Box bg={cardBg} borderRadius="xl" borderWidth="1px"
        borderColor={borderColor} p={6}>
        <HStack mb={5} spacing={2}>
          <Icon as={FaHeadset} color="blue.500" />
          <Text fontWeight="bold" fontSize="lg">Submit a Support Query</Text>
        </HStack>

        <Divider mb={5} />

        <VStack spacing={5} align="stretch">

          {currentStudent && (
            <HStack spacing={4} p={3} bg={prefilledBg}
              borderRadius="md" fontSize="sm">
              <Text color={mutedText}>Submitting as:</Text>
              <Text fontWeight="medium">
                {currentStudent.givenName} {currentStudent.surname}
              </Text>
              <Badge colorScheme="blue" fontSize="xs">{currentStudent.email}</Badge>
            </HStack>
          )}

          <FormControl isRequired>
            <FormLabel fontSize="sm">Category</FormLabel>
            <Select
              placeholder="Select the category that best fits your query"
              value={category}
              onChange={e => setCategory(e.target.value)}
              size="md"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Describe your query</FormLabel>
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Please describe your issue or question in as much detail as possible. Include any error messages, the page you were on, and what you were trying to do."
              rows={6}
              resize="vertical"
            />
            <Text fontSize="xs" color={mutedText} mt={1}>
              {message.length} characters — minimum 20 required
            </Text>
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">How would you like us to respond?</FormLabel>
            <RadioGroup value={preferredChannel} onChange={setPreferredChannel}>
              <Stack direction="row" spacing={6}>
                <Radio value="email" colorScheme="blue">
                  Email
                </Radio>
                <Radio value="phone" colorScheme="blue">
                  Phone call
                </Radio>
              </Stack>
            </RadioGroup>
          </FormControl>

          {preferredChannel === "phone" && (
            <FormControl isRequired>
              <FormLabel fontSize="sm">Preferred call time</FormLabel>
              <Select
                placeholder="Select a time slot"
                value={phoneSlot}
                onChange={e => setPhoneSlot(e.target.value)}
              >
                {PHONE_SLOTS.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </Select>
              {currentStudent?.contactNo && (
                <Text fontSize="xs" color={mutedText} mt={1}>
                  We'll call you on the number registered to your account:{" "}
                  <strong>{currentStudent.contactNo}</strong>
                </Text>
              )}
            </FormControl>
          )}

          <Button
            colorScheme="blue"
            size="md"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Submitting..."
            alignSelf="flex-start"
            px={8}
          >
            Submit Query
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default StudentSupportPage;